import { getUsersForAsignment } from '@/api/order-api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { Customer } from '@/types/customer-type';
import { OrderResponse } from '@/types/order-type';
import { User } from '@/types/user';
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';

export type AssignUserDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: OrderResponse;
  customer: Customer;
  onSubmit: (userId: string, orderId: string) => Promise<void>;
};

export function AssignUserDialog({
  open,
  onOpenChange,
  order,
  customer,
  onSubmit,
}: AssignUserDialogProps) {
  const [selectedUser, setSelectedUser] = useState<string | undefined>(undefined);
  const [users, setUsers] = useState<User[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const fetchUsers = async () => {
    try {
      const response = await getUsersForAsignment();
      if (response.success) {
        setUsers(response.data);
      } else {
        toast({
          title: 'Error',
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  // Reset selected user when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedUser(undefined);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      await onSubmit(selectedUser, order.id);
      onOpenChange(false);
    } catch (error) {
      console.error('Error assigning user:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] sm:max-w-sm w-full p-5 sm:p-6">
        <DialogHeader className="pb-3 space-y-1">
          <DialogTitle className="text-lg font-semibold">Assign User</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Select a user to assign to this order
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Order summary */}
          {order && (
            <Card className="border bg-muted/30">
              <CardContent className="p-3 space-y-2.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                      Order
                    </span>
                    <span className="text-xs font-semibold text-foreground">#{order.orderNumber}</span>
                  </div>
                  <div className="text-sm font-bold text-primary">
                    {order.currency ?? 'USD'}{' '}
                    {order.totalAmount &&
                      order.totalAmount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </div>
                </div>
                {customer.name && (
                  <>
                    <Separator className="bg-border/60" />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                        Customer
                      </span>
                      <span className="text-xs font-medium text-foreground truncate max-w-[60%]">{customer.name}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* User selection */}
          <div className="space-y-1.5">
            <Label htmlFor="user-select" className="text-xs font-medium text-foreground">
              Select User
            </Label>
            <Select value={selectedUser} onValueChange={setSelectedUser} disabled={submitting}>
              <SelectTrigger id="user-select" className="h-9 text-sm w-full bg-background">
                <SelectValue placeholder="Choose a user" />
              </SelectTrigger>
              <SelectContent className="z-[1502] max-h-[280px]">
                {users.length === 0 ? (
                  <div className="py-4 text-center text-xs text-muted-foreground">
                    No users available
                  </div>
                ) : (
                  users.map((user: User) => (
                    <SelectItem
                      key={user.username}
                      value={user.username}
                      className="py-2 px-2.5 focus:bg-accent/50 focus:text-accent-foreground cursor-pointer"
                    >
                      <div className="flex items-center gap-2.5 w-full min-w-0">
                        {/* Avatar */}
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-bold text-primary">
                          {user.firstName?.[0] || ''}
                          {user.lastName?.[0] || ''}
                        </div>

                        {/* Name and Username */}
                        <div className="flex flex-col items-start overflow-hidden min-w-0 flex-1">
                          <span className="text-xs font-semibold truncate w-full text-foreground">
                            {user.firstName || ''} {user.lastName || ''}
                          </span>
                          <span className="text-[10px] text-muted-foreground font-mono opacity-75 uppercase tracking-tighter truncate w-full">
                            {user.username}
                          </span>
                        </div>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="pt-3 gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="w-full sm:w-auto h-9 text-sm order-2 sm:order-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting || !selectedUser}
            className="w-full sm:w-auto h-9 text-sm order-1 sm:order-2"
          >
            {submitting ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
